/**
 * @module Badge
 *
 * Small status indicators and labels for highlighting information.
 * Supports multiple color variants and sizes for different contexts.
 *
 * @see {@link https://ui.shadcn.com/docs/components/badge Shadcn Badge}
 *
 * @example
 * // Basic badge
 * <Badge>New</Badge>
 *
 * @example
 * // Badge variants
 * <Badge variant="brand">Premium</Badge>
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Expired</Badge>
 *
 * @example
 * // As a child element (e.g., wrapping a link)
 * <Badge asChild variant="outline">
 *   <a href="/pricing">View Plans</a>
 * </Badge>
 */
import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "../lib/utils";

/**
 * CVA variants for Badge styling.
 * Includes color variants matching Button component for consistency.
 */
const badgeVariants = cva("inline-flex items-center rounded-full border transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 dark:focus-visible:ring-gray-300", {
  variants: {
    variant: {
      // Matching button variants (without hover/disabled states)
      subtle: "border-1 border-gray-150 border-solid bg-white text-gray-950 dark:border-gray-800 dark:bg-gray-950 dark:text-white",
      brand: "border-1 border-brand-primary border-solid bg-brand-primary text-white",
      brandSecondary: "border-1 border-brand-secondary border-solid bg-brand-secondary text-brand-primary-hover dark:border-gray-900 dark:bg-gray-900 dark:text-white",
      bold: "border-gray-950 bg-gray-950 text-white dark:border-white  dark:bg-white dark:text-gray-950",
      light: "border-transparent bg-gray-100 text-gray-950  dark:bg-gray-800 dark:text-white",
      transparent: "border-transparent bg-transparent text-gray-950 dark:text-white",
      contrast: "border-white bg-white text-gray-950",
      success: "border-green-600 bg-green-600 text-white dark:border-green-500 dark:bg-green-500",
      error: "border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-500",
      primary: "border-gray-900 bg-gray-900 text-white dark:border-gray-50 dark:bg-gray-50 dark:text-gray-950",
      secondary: "border-gray-150 bg-gray-100 text-gray-950 dark:border-gray-800 dark:bg-gray-800 dark:text-white",
      destructive: "border-red bg-red text-white",
      white: "border-gray-200 bg-white text-brand-primary",
      brandLight: "border-brand-secondary bg-brand-secondary text-brand-primary-hover",
      outline: "border border-gray-200 border-solid bg-transparent text-gray-800 focus-visible:ring-gray-500 dark:border-gray-800 dark:bg-transparent dark:text-white dark:focus-visible:ring-gray-400"
    },
    size: {
      default: "h-8 px-4 py-2 leading-8",
      xs: "h-5 gap-1.5 px-2 text-xs leading-5",
      sm: "h-6 px-2.5 text-sm leading-6",
      lg: "h-10 gap-4 px-8 text-lg leading-10",
      icon: "h-8 w-8 leading-8"
    },
    shadow: {
      true: "shadow",
      false: ""
    },
    strong: {
      true: "font-semibold",
      false: ""
    }
  },
  defaultVariants: {
    variant: "bold",
    size: "default",
    shadow: false,
    strong: false
  }
});

/**
 * Props for the Badge component.
 *
 * @property variant - Color scheme: `brand`, `brandSecondary`, `success`, `error`, `subtle`, `bold`, etc.
 * @property size - Size variant: `xs`, `sm`, `default`, `lg`, `icon`
 * @property shadow - Add drop shadow when true
 * @property strong - Use semibold font weight when true
 * @property asChild - Render as child element (uses Radix Slot)
 * @property ref - Ref to the underlying div element
 */
export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Displays a small label or status indicator.
 *
 * Common use cases:
 * - Subscription status (Free, Premium)
 * - Item counts
 * - Status indicators (New, Sale, Popular)
 * - Tags and categories
 */
const Badge = ({
  className,
  variant,
  size,
  shadow,
  strong,
  asChild = false,
  ref,
  ...props
}: BadgeProps) => {
  const Comp = asChild ? SlotPrimitive.Slot : "div";
  return <Comp className={cn(badgeVariants({
    variant,
    size,
    shadow,
    strong
  }), className)} ref={ref} {...props} data-sentry-element="Comp" data-sentry-component="Badge" data-sentry-source-file="badge.tsx" />;
};
export { Badge, badgeVariants };